import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Button,
  FormHelperText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import DataTable from "../../components/DataTable";
import { motion } from "framer-motion";
import { StockResponse, StockStatusResponse } from "../../types/api.types";
import { formatDate } from "../../utils/date";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HistoryIcon from "@mui/icons-material/History";
import CancelIcon from "@mui/icons-material/Cancel";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { stockService } from "../../services/stock/stockService";
import FilterStocks, {
  FilterStocksFormData,
} from "../../components/FilterStocks";
import { useSnackbar } from "../../context/SnackbarContext";

const StocksPage = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isCancelling, setIsCancelling] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState(0);

  const [searchFilters, setSearchFilters] =
    useState<FilterStocksFormData | null>(null);

  const [states, setStates] = useState<StockStatusResponse[]>([]);

  // Estados para el modal de historial
  const [historyModalOpen, setHistoryModalOpen] = useState<boolean>(false);
  const [selectedStockId, setSelectedStockId] = useState<string>("");
  const [historyData, setHistoryData] = useState<any[]>([]);
  const [historyTotal, setHistoryTotal] = useState<number>(0);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [historyPage, setHistoryPage] = useState<number>(0);
  const [historyRowsPerPage] = useState<number>(10);
  const [historyDateFrom, setHistoryDateFrom] = useState<string>("");
  const [historyDateTo, setHistoryDateTo] = useState<string>("");
  const [historyDateError, setHistoryDateError] = useState<string>("");

  const [historyDetailModalOpen, setHistoryDetailModalOpen] =
    useState<boolean>(false);
  const [historyDetailData, setHistoryDetailData] = useState<any[]>([]);
  const [historyDetailLoading, setHistoryDetailLoading] =
    useState<boolean>(false);

  // Estados para el modal de anulación
  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);
  const [selectedCancelStockId, setSelectedCancelStockId] =
    useState<string>("");
  const [cancelObservation, setCancelObservation] = useState<string>("");
  const [cancelObservationError, setCancelObservationError] =
    useState<string>("");

  const columns = [
    {
      header: "Nro. Correlativo",
      key: "consecutiveNumber",
    },
    {
      header: "Fecha estimada de llegada",
      key: "estimatedArribalDate",
      render: (value: any) => (
        <Typography>{formatDate(value?.estimatedArribalDate)}</Typography>
      ),
    },
    {
      header: "Fecha de llegada",
      key: "arrivalDate",
      render: (value: any) => (
        <Typography>{formatDate(value?.arribalDate)}</Typography>
      ),
    },
    {
      header: "Sociedad",
      key: "company",
    },
    {
      header: "Código Interno",
      key: "code",
    },
    {
      header: "Estado",
      key: "status",
    },
    {
      header: "Acciones",
      key: "actions",
      render: (row: any) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <HistoryIcon
            sx={{ cursor: "pointer", color: "info.main" }}
            onClick={() => handleOpenHistoryModal(row.stockHeaderId)}
            titleAccess="Ver Historial"
          />
          <VisibilityIcon
            sx={{ cursor: "pointer", color: "success.main" }}
            onClick={() => navigate(`/stocks/view/${row.stockHeaderId}`)}
            titleAccess="Ver"
          />
          {row?.status !== "Anulado" && row?.status !== "Arribado" && (
            <EditIcon
              sx={{ cursor: "pointer", color: "success.main" }}
              onClick={() => navigate(`/stocks/edit/${row.stockHeaderId}`)}
              titleAccess="Editar"
            />
          )}

          {row?.status !== "Arribado" && row?.status !== "Anulado" && (
            <LocalShippingIcon
              sx={{ cursor: "pointer", color: "warning.main" }}
              onClick={() => handleArrived(row.stockHeaderId)}
              titleAccess="Marcar como Arribado"
            />
          )}

          {row?.status !== "Anulado" && row?.status !== "Arribado" && (
            <CancelIcon
              sx={{ cursor: "pointer", color: "error.main" }}
              onClick={() => handleOpenCancelModal(row.stockHeaderId)}
              titleAccess="Anular Pedido"
            />
          )}
        </Box>
      ),
    },
  ];

  const [stocks, setStocks] = useState<StockResponse>();

  const getStocks = async (filters?: FilterStocksFormData | null) => {
    setIsLoading(true);
    const response = await stockService.getStocks(
      currentPage,
      10,
      filters?.code,
      filters?.statusId,
      filters?.dateFrom,
      filters?.dateTo,
    );
    setStocks(response);
    setIsLoading(false);
  };

  useEffect(() => {
    getStocks(searchFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchFilters]);

  const handleFilter = (data: FilterStocksFormData) => {
    setSearchFilters(data);
    setCurrentPage(0);
  };

  const handleCancelFilter = () => {
    setSearchFilters(null);
    setCurrentPage(0);
  };

  const onGetStates = async () => {
    setIsLoading(true);
    const response = await stockService.getStatuses();
    setStates(response);
    setIsLoading(false);
  };

  useEffect(() => {
    onGetStates();
  }, []);

  // Funciones para manejar el modal de historial
  const handleOpenHistoryModal = async (stockId: string) => {
    setSelectedStockId(stockId);
    setHistoryModalOpen(true);
    setHistoryPage(0);
    await fetchHistory(stockId, 0);
  };

  const handleCloseHistoryModal = () => {
    setHistoryModalOpen(false);
    setSelectedStockId("");
    setHistoryData([]);
    setHistoryTotal(0);
    setHistoryDateFrom("");
    setHistoryDateTo("");
    setHistoryPage(0);
  };

  //Funciones para manejar el modal de historial de detalle
  const handleOpenHistoryDetailModal = async (stockHistoryId: string) => {
    setHistoryDetailModalOpen(true);
    setHistoryDetailData([]);
    try {
      setHistoryDetailLoading(true);
      const response = await stockService.getHistoryItem(stockHistoryId);
      setHistoryDetailData(response || []);
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al cargar el detalle",
        "error",
        4000,
      );
      setHistoryDetailModalOpen(false);
    } finally {
      setHistoryDetailLoading(false);
    }
  };

  const handleCloseHistoryDetailModal = () => {
    setHistoryDetailModalOpen(false);
    setHistoryDetailData([]);
  };

  const fetchHistory = async (
    stockId?: string,
    page?: number,
    dateFrom?: string,
    dateTo?: string,
  ) => {
    try {
      setHistoryLoading(true);
      const response = await stockService.getHistory(
        stockId || selectedStockId,
        page !== undefined ? page : historyPage,
        historyRowsPerPage,
        dateFrom !== undefined ? dateFrom : historyDateFrom,
        dateTo !== undefined ? dateTo : historyDateTo,
      );
      setHistoryData(response?.stocks || response || []);
      setHistoryTotal(response?.total || 0);
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al cargar historial",
        "error",
        4000,
      );
      setHistoryData([]);
      setHistoryTotal(0);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleFilterHistory = () => {
    setHistoryDateError("");

    if (
      (historyDateFrom && !historyDateTo) ||
      (!historyDateFrom && historyDateTo)
    ) {
      setHistoryDateError("Debe seleccionar ambas fechas: Desde y Hasta");
      return;
    }

    if (historyDateFrom && historyDateTo) {
      const from = new Date(historyDateFrom);
      const to = new Date(historyDateTo);

      if (from > to) {
        setHistoryDateError(
          "La fecha Desde no puede ser posterior a la fecha Hasta",
        );
        return;
      }
    }

    setHistoryPage(0);
    fetchHistory(selectedStockId, 0, historyDateFrom, historyDateTo);
  };

  const handleClearHistoryFilters = () => {
    setHistoryDateFrom("");
    setHistoryDateTo("");
    setHistoryPage(0);
    fetchHistory(selectedStockId, 0, "", "");
  };

  const handleHistoryPageChange = (page: number) => {
    const newPage = page - 1; // DataTable usa páginas basadas en 1, API usa basadas en 0
    setHistoryPage(newPage);
    fetchHistory(selectedStockId, newPage);
  };

  // Funciones para manejar el modal de anulación
  const handleOpenCancelModal = (stockId: string) => {
    setSelectedCancelStockId(stockId);
    setCancelModalOpen(true);
    setCancelObservation("");
    setCancelObservationError("");
  };

  const handleCloseCancelModal = () => {
    setCancelModalOpen(false);
    setSelectedCancelStockId("");
    setCancelObservation("");
    setCancelObservationError("");
  };

  const handleConfirmCancel = async () => {
    // Validar que haya observación
    if (!cancelObservation.trim()) {
      setCancelObservationError("La observación es requerida");
      return;
    }

    try {
      setIsCancelling(true);
      await stockService.cancelStock(selectedCancelStockId, cancelObservation);

      showSnackbar("Pedido anulado exitosamente", "success", 3000);
      handleCloseCancelModal();
      getStocks(searchFilters); // Recargar la lista
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al anular el pedido",
        "error",
        4000,
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const handleArrived = async (stockId: string) => {
    navigate(`/stocks/arribal/${stockId}`);
  };

  const historyColumns = [
    { header: "Nro.", key: "consecutiveNumber" },
    {
      header: "Código",
      key: "code",
    },
    {
      header: "Estado Anterior",
      key: "originalStockStatus",
    },
    {
      header: "Estado Actual",
      key: "updateStockStatus",
    },
    {
      header: "Fecha Estimada Anterior",
      key: "originalEstimatedArribalDateLocal",
      render: (row: any) => (
        <Typography>
          {row?.originalEstimatedArribalDateLocal
            ? formatDate(row.originalEstimatedArribalDateLocal)
            : "-"}
        </Typography>
      ),
    },
    {
      header: "Fecha Estimada",
      key: "estimatedArribalDateLocal",
      render: (row: any) => (
        <Typography>
          {row?.estimatedArribalDateLocal
            ? formatDate(row.estimatedArribalDateLocal)
            : "-"}
        </Typography>
      ),
    },
    {
      header: "Fecha Llegada Anterior",
      key: "originalArribalDateLocal",
      render: (row: any) => (
        <Typography>
          {row?.originalArribalDateLocal
            ? formatDate(row.originalArribalDateLocal)
            : "-"}
        </Typography>
      ),
    },
    {
      header: "Fecha Llegada",
      key: "arribalDateLocal",
      render: (row: any) => (
        <Typography>
          {row?.arribalDateLocal ? formatDate(row.arribalDateLocal) : "-"}
        </Typography>
      ),
    },
    {
      header: "Fecha",
      key: "createDateLocal",
      render: (row: any) => (
        <Typography>
          {row?.createDateLocal ? formatDate(row.createDateLocal) : "-"}
        </Typography>
      ),
    },
    {
      header: "Actualizado Por",
      key: "userName",
    },
    {
      header: "Acciones",
      key: "actions",
      render: (row: any) => (
        <VisibilityIcon
          sx={{ cursor: "pointer", color: "success.main" }}
          onClick={() => handleOpenHistoryDetailModal(row.stockHistoryId)}
          titleAccess="Ver Detalle"
        />
      ),
    },
  ];

  return (
    <Box>
      <Card>
        <CardContent>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h4">Stocks</Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate("/stocks/create")}
              >
                Crear Stock
              </Button>
            </Box>
          </motion.div>

          <Divider sx={{ my: 3 }} />

          <FilterStocks
            searchFilters={searchFilters}
            onFilter={handleFilter}
            onCancel={handleCancelFilter}
            statuses={states}
          />

          <Divider sx={{ my: 3 }} />

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
            style={{ marginTop: 2 }}
          >
            <DataTable
              isLoading={isLoading}
              columns={columns}
              total={stocks?.total || 0}
              data={stocks?.stockHeaderResponses || []}
              page={currentPage + 1}
              onChange={(page) => {
                setCurrentPage(page - 1);
              }}
            />
          </motion.div>
        </CardContent>
      </Card>

      {/* Modal de Anulación */}
      <Dialog
        open={cancelModalOpen}
        onClose={handleCloseCancelModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" component="div">
            Anular Pedido
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Observación"
              multiline
              rows={4}
              fullWidth
              value={cancelObservation}
              onChange={(e) => {
                setCancelObservation(e.target.value);
                setCancelObservationError("");
              }}
              error={!!cancelObservationError}
              helperText={cancelObservationError}
              placeholder="Ingrese el motivo de la anulación..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseCancelModal}
            variant="outlined"
            disabled={isCancelling}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            color="error"
            loading={isCancelling}
            disabled={isCancelling}
          >
            Confirmar Anulación
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Historial */}
      <Dialog
        open={historyModalOpen}
        onClose={handleCloseHistoryModal}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" component="div">
            Historial de Stock
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Filtros de fecha */}
          <Box sx={{ mb: 3, mt: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ alignItems: "center" }}
            >
              <TextField
                label="Fecha Desde"
                type="date"
                value={historyDateFrom}
                onChange={(e) => {
                  setHistoryDateFrom(e.target.value);
                  setHistoryDateError("");
                }}
                error={!!historyDateError}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
              <TextField
                label="Fecha Hasta"
                type="date"
                value={historyDateTo}
                onChange={(e) => {
                  setHistoryDateTo(e.target.value);
                  setHistoryDateError("");
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!historyDateError}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={handleFilterHistory}
                sx={{ minWidth: 120, height: 40 }}
              >
                Filtrar
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearHistoryFilters}
                sx={{ minWidth: 120, height: 40 }}
              >
                Limpiar
              </Button>
            </Stack>
            <Box sx={{ mt: 1 }}>
              {historyDateError && (
                <FormHelperText error>{historyDateError}</FormHelperText>
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* DataTable de historial */}
          <DataTable
            isLoading={historyLoading}
            columns={historyColumns}
            total={historyTotal}
            data={historyData}
            page={historyPage + 1}
            onChange={handleHistoryPageChange}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseHistoryModal} variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Detalle de Historial */}
      <Dialog
        open={historyDetailModalOpen}
        onClose={handleCloseHistoryDetailModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" component="div">
            Detalle del Historial
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DataTable
            isLoading={historyDetailLoading}
            columns={[
              { header: "Código", key: "code" },
              { header: "Descripción", key: "description" },
              {
                header: "Cantidad Anterior",
                key: "originalQuantity",
                render: (row: any) => (
                  <Typography>{row?.originalQuantity ?? "-"}</Typography>
                ),
              },
              {
                header: "Cantidad Actual",
                key: "quantity",
                render: (row: any) => (
                  <Typography>{row?.quantity ?? "-"}</Typography>
                ),
              },
              {
                header: "Diferencia",
                key: "diff",
                render: (row: any) => {
                  const diff =
                    (row?.quantity ?? 0) - (row?.originalQuantity ?? 0);
                  const color =
                    diff > 0
                      ? "success.main"
                      : diff < 0
                        ? "error.main"
                        : "text.primary";
                  return (
                    <Typography sx={{ color, fontWeight: 600 }}>
                      {row?.originalQuantity
                        ? diff > 0
                          ? `+${diff}`
                          : diff
                        : "Nuevo"}
                    </Typography>
                  );
                },
              },
            ]}
            total={historyDetailData.length}
            data={historyDetailData}
            page={1}
            onChange={() => {}}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseHistoryDetailModal} variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StocksPage;
